import {
  getCurrentUser,
  signIn,
  signOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import {
  parseAuthError,
  shouldRetry,
  calculateRetryDelay,
  AuthError,
} from "./authErrors";

export interface AuthUser {
  username: string;
  email?: string;
  sub: string;
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresAt?: number;
}

export interface AuthRetryState {
  attemptNumber: number;
  lastError: AuthError | null;
  isRetrying: boolean;
}

/**
 * Check if user is currently authenticated
 */
export const checkAuthStatus = async (): Promise<boolean> => {
  try {
    await getCurrentUser();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentAuthUser = async (): Promise<AuthUser | null> => {
  try {
    const user = await getCurrentUser();
    return {
      username: user.username,
      email: user.signInDetails?.loginId,
      sub: user.userId,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Sign in user with username and password with retry logic
 */
export const signInUser = async (
  username: string,
  password: string,
  retryState?: AuthRetryState,
): Promise<void> => {
  const currentAttempt = retryState?.attemptNumber || 1;

  try {
    const result = await signIn({
      username,
      password,
    });

    if (result.isSignedIn) {
      return;
    } else {
      throw new Error("Sign in was not completed");
    }
  } catch (error) {
    const authError = parseAuthError(error);

    // If this is a retryable error and we haven't exceeded max attempts
    if (authError.retryable && shouldRetry(authError.type, currentAttempt)) {
      const delay = calculateRetryDelay(authError.type, currentAttempt);

      // Wait for the calculated delay
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry with incremented attempt number
      return signInUser(username, password, {
        attemptNumber: currentAttempt + 1,
        lastError: authError,
        isRetrying: true,
      });
    }

    // If not retryable or max attempts exceeded, throw the parsed error
    const errorToThrow = new Error(authError.message);
    (errorToThrow as any).authError = authError;
    (errorToThrow as any).attemptNumber = currentAttempt;
    throw errorToThrow;
  }
};

/**
 * Sign out current user
 */
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut();
  } catch (error) {
    throw error;
  }
};

/**
 * Get current auth tokens
 */
export const getAuthTokens = async (): Promise<AuthTokens | null> => {
  try {
    const session = await fetchAuthSession();

    if (session.tokens) {
      const accessToken = session.tokens.accessToken;
      const idToken = session.tokens.idToken;

      return {
        accessToken: accessToken.toString(),
        idToken: idToken?.toString() || "",
        expiresAt: accessToken.payload.exp
          ? accessToken.payload.exp * 1000
          : undefined,
      };
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Check if tokens are expired or will expire soon (within 5 minutes)
 */
export const areTokensExpired = (tokens: AuthTokens | null): boolean => {
  if (!tokens || !tokens.expiresAt) {
    return true;
  }

  const now = Date.now();
  const fiveMinutesFromNow = now + 5 * 60 * 1000; // 5 minutes in milliseconds

  return tokens.expiresAt <= fiveMinutesFromNow;
};

/**
 * Refresh authentication tokens with retry logic
 */
export const refreshAuthTokens = async (
  retryState?: AuthRetryState,
): Promise<AuthTokens | null> => {
  const currentAttempt = retryState?.attemptNumber || 1;

  try {
    // Force refresh the session
    const session = await fetchAuthSession({ forceRefresh: true });

    if (session.tokens) {
      const accessToken = session.tokens.accessToken;
      const idToken = session.tokens.idToken;

      return {
        accessToken: accessToken.toString(),
        idToken: idToken?.toString() || "",
        expiresAt: accessToken.payload.exp
          ? accessToken.payload.exp * 1000
          : undefined,
      };
    }

    return null;
  } catch (error) {
    const authError = parseAuthError(error);

    // If this is a retryable error and we haven't exceeded max attempts
    if (authError.retryable && shouldRetry(authError.type, currentAttempt)) {
      const delay = calculateRetryDelay(authError.type, currentAttempt);

      // Wait for the calculated delay
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry with incremented attempt number
      return refreshAuthTokens({
        attemptNumber: currentAttempt + 1,
        lastError: authError,
        isRetrying: true,
      });
    }

    console.error("Token refresh failed:", error);
    return null;
  }
};

/**
 * Get authorization header for API requests
 * This function ensures tokens are valid and refreshes them if needed
 */
export const getAuthHeader = async (): Promise<string | null> => {
  try {
    const tokens = await getAuthTokens();
    if (!tokens) {
      return null;
    }

    // Check if tokens are expired and refresh if needed
    if (areTokensExpired(tokens)) {
      const refreshedTokens = await refreshAuthTokens();
      if (refreshedTokens) {
        return `Bearer ${refreshedTokens.idToken}`;
      }
      return null;
    }

    return `Bearer ${tokens.idToken}`;
  } catch (error) {
    console.error("Error getting auth header:", error);
    return null;
  }
};
